/**
 * Results Page - Agent Attack Surface visualization
 * Displays workflow graph and vulnerability analysis
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { DownloadIcon } from '../components/Icons';
import {
  AttackSurfaceGraph,
  ToolsOverviewSection,
  VulnerabilityFindingsSection,
} from '../features/attackSurface';
import './ResultsPage.css';
import { useLocation } from 'react-router-dom';
import { GraphDefinition, ToolOverview, VulnerabilityFinding, RiskLevel } from '../features/attackSurface/types';
import yaml from 'js-yaml';
import { transformToolsOverview, transformVulnerabilityFindings } from '../features/attackSurface/utils/transformResults';
import BackgroundWrapper2 from '../components/BackgroundWrapper2';
import AttackSurfaceScore from '../features/attackSurface/components/graph/AttackSurfaceScore';
import RiskLevelChip from '../features/attackSurface/components/graph/RiskLevelChip';
import { exportGraphAndWorkflow } from '../utils/exportUtils';
import FeedbackModal from '../components/FeedbackModal';
import { useSendFeedbackMutation } from '../apiSlice';
import Tooltip from '@mui/material/Tooltip';


const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'workflow' | 'vulnerabilities'>('workflow');
  const location = useLocation();

  // Get the uploaded file name from location state or localStorage
  const [uploadedFileName, setUploadedFileName] = useState(() => {
    const stateFileName = location.state?.fileName;
    if (stateFileName) {
      localStorage.setItem('uploadedFileName', stateFileName);
      return stateFileName;
    }
    return localStorage.getItem('uploadedFileName') || '';
  });
  
    // Try to get data from location.state first, then fallback to localStorage
  const [graphData, setGraphData] = useState(() => {
    if (location.state?.graphData) {
      // Save to localStorage when coming from navigation
      localStorage.setItem('resultsGraphData', JSON.stringify(location.state.graphData));
      return location.state.graphData;
    }
    // Fallback to localStorage on refresh
    const saved = localStorage.getItem('resultsGraphData');
    return saved ? JSON.parse(saved) : null;
  });

  const [fileExt, setFileExt] = useState(() => {
    if (location.state?.fileExt) {
      localStorage.setItem('resultsFileExt', location.state.fileExt);
      return location.state.fileExt;
    }
    return localStorage.getItem('resultsFileExt') || 'json';
  });

  const toolsOverview = graphData ? transformToolsOverview(graphData) : null;
  const vulnerabilityFindings = graphData ? transformVulnerabilityFindings(graphData) : null;

  useEffect(() => {
      if(activeTab === 'workflow'){
        setFocusedVulnId(null);
      }
    }, [activeTab]);

  const performExport = async () => {
    if (!graphData?.modified_workflow) {
      alert('Error - No modified workflow to export!');
      return;
    }

    const ext = fileExt || 'json';
    await exportGraphAndWorkflow(graphData.modified_workflow, ext, 'modified_workflow');
  };

  const [focusedVulnId, setFocusedVulnId] = useState<string | null>(null);
  const handleVulnerabilityClick = (vulnId: string) => {
    setActiveTab('vulnerabilities');
    setFocusedVulnId(vulnId);
  }

  function getRiskSummary(findings: any[] | null) {
    let critical = 0, high = 0, medium = 0, low = 0;

    if (findings && Array.isArray(findings)) {
      findings.forEach(f => {
        switch (f.severity) {
          case "Critical": critical++; break;
          case "High": high++; break;
          case "Medium": medium++; break;
          case "Low": low++; break;
          default: break;
        }
      });
    }

    // Determine overall riskLevel (highest present)
    let riskLevel: "Critical" | "High" | "Medium" | "Low" = "Low";
    if (critical > 0) riskLevel = "Critical";
    else if (high > 0) riskLevel = "High";
    else if (medium > 0) riskLevel = "Medium";
    return { riskLevel, critical, high, medium, low };
  }

  function getNodeTypeCounts(graphData: GraphDefinition | null) {
    if (!graphData) {
      return { vulnerabilities: 0, agents: 0, tools: 0, mcpServers: 0 };
    }

    // Count tools
    const tools = graphData.tools?.length || 0;
    
    // Count agents
    const agents = graphData.agents?.length || 0;
    
    // Count MCP servers (if they exist in your data structure)
    const mcpServers = (graphData as any).mcp_servers?.length || 0;
    
    // Count vulnerabilities from all sources
    let vulnerabilities = 0;
    
    // From tools
    graphData.tools?.forEach(tool => {
      vulnerabilities += tool.vulnerabilities?.length || 0;
    });
    
    // From agents
    graphData.agents?.forEach(agent => {
      vulnerabilities += agent.vulnerabilities?.length || 0;
    });

    return { vulnerabilities, agents, tools, mcpServers };
  }

  const riskSummary = getRiskSummary(vulnerabilityFindings);
  const nodeTypeCounts = getNodeTypeCounts(graphData);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [sendFeedback] = useSendFeedbackMutation();

  const handleExport = () => {
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async (feedback: string) => {
    try {
      await sendFeedback({ feedback, fileName: uploadedFileName }).unwrap();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }

    setShowFeedbackModal(false);
    await performExport();
  };

  const handleFeedbackSkip = async () => {
    setShowFeedbackModal(false);
    await performExport();
  };

  const replaceUnderScore = (text: string) => {
    return text.replaceAll("_", " ")
  }

  return (
    <BackgroundWrapper2>
      <div className={`results-container ${activeTab === 'vulnerabilities' ? 'results-container--vulnerabilities' : 'results-container--workflow'}`}>
        <div className="results-header-wrapper">
          <Header />
        </div>
        <div className="results-content-card">
         {/* Sub-header with title and tabs */}
          <div className="results-sub-header">
            <div className="results-title-row">
              <Tooltip title={replaceUnderScore((uploadedFileName.split("."))[0])} placement="bottom" arrow>
                <h1 className="results-title">{replaceUnderScore((uploadedFileName.split("."))[0])}</h1>
              </Tooltip>
              <h1 className="results-title-2">Attack Surface</h1>
              <RiskLevelChip
                riskLevel={riskSummary.riskLevel}
                vulnerabilityCount={nodeTypeCounts.vulnerabilities}
                critical={riskSummary.critical}
                high={riskSummary.high}
                medium={riskSummary.medium}
                low={riskSummary.low}
              />
            </div>
            
            <AttackSurfaceScore
              nodeTypes={nodeTypeCounts}
            />

            <div className="results-tabs">
              <button
                onClick={() => setActiveTab('workflow')}
                className={`results-tab-button ${activeTab === 'workflow' ? 'active' : ''}`} // Makes it 'appear' active (in the css - with background color)
              >
                Map
              </button>
              <button
                onClick={() => setActiveTab('vulnerabilities')}
                className={`results-tab-button ${activeTab === 'vulnerabilities' ? 'active' : ''}`}
              >
                Table
              </button>
            </div>
          </div>

          <main className="results-main">
            {activeTab === 'workflow' ? (
              /* Workflow Tab - Graph Visualization: */
              <div className="results-workflow-section">
                <AttackSurfaceGraph 
                  graphData={graphData}
                  onVulnerabilityClick={handleVulnerabilityClick}
                />
              </div>
            ) : (
              /*Vulnerabilities Tab - Tools Overview & Findings: */
              <div className="results-vulnerabilities-tab">
                {/* Tools Overview Section */}
                <ToolsOverviewSection tools={toolsOverview || []} />

                {/* Detailed Vulnerability Findings Section */}
                <VulnerabilityFindingsSection 
                  vulnerabilities={vulnerabilityFindings || []}
                  focusedVulnId={focusedVulnId}
                  onClearFocus={() => setFocusedVulnId(null)}
                />
              </div>
            )}

            {/* Export Button */}
            <div className="results-export-section">
              <button onClick={handleExport} className="results-export-button">
                <DownloadIcon />
                Export
              </button>
            </div>
          </main>
        </div>

        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
          onSkip={handleFeedbackSkip}
        />
      </div>
    </BackgroundWrapper2>
  );
};

export default ResultsPage;